import { useState, type ReactNode } from "react";
import type { AwaitingCountersignature } from "../api/types.js";
import { Async, Failure } from "../components/Async.jsx";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { useSubmit } from "../app/use-submit.js";
import { longDate } from "../lib/format.js";

/**
 * The agreements a customer has signed and the company has not.
 *
 * WHY THIS SCREEN EXISTS. The company used to be deemed to have signed the
 * instant the customer did -- written in the same breath as their signature,
 * recorded as "applied automatically". Nobody here ever looked at an
 * agreement before it was executed. Somebody does now, and this is where.
 *
 * Between the two signatures an agreement is half-executed. The customer
 * already holds a copy saying exactly that; countersigning produces the
 * executed document and sends it, superseding the one they hold.
 */
export function CountersignScreen(): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<AwaitingCountersignature[]>(
    () => client.awaitingCountersignature(),
    [client],
  );

  return (
    <section className="screen">
      <h1>Agreements to countersign</h1>
      <p className="hint small">
        Signed by the customer and waiting for the company. Until you
        countersign, the agreement is signed by one party and the customer&rsquo;s
        copy says so.
      </p>
      <Async state={state} what="agreements waiting to be countersigned" reload={reload}>
        {(waiting) => <Body waiting={waiting} reload={reload} />}
      </Async>
    </section>
  );
}

function Body({
  waiting,
  reload,
}: {
  readonly waiting: readonly AwaitingCountersignature[];
  readonly reload: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { submit, busy, error } = useSubmit();
  /** Which one is being signed, so two presses cannot both act. */
  const [signing, setSigning] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const countersign = async (envelopeId: string): Promise<void> => {
    setSigning(envelopeId);
    try {
      await submit(async () => {
        const res = await client.countersign(envelopeId);
        setDone(res.emailed ? "Countersigned, and the executed copy sent." : "Countersigned.");
        reload();
      });
    } finally {
      setSigning(null);
    }
  };

  if (waiting.length === 0) {
    return (
      <>
        {done ? (
          <p className="notice" role="status">
            {done}
          </p>
        ) : null}
        <p className="hint small">Nothing is waiting. Every signed agreement has been countersigned.</p>
      </>
    );
  }

  return (
    <>
      {done ? (
        <p className="notice" role="status">
          {done}
        </p>
      ) : null}
      <ul className="plain-list" data-testid="awaiting-countersignature">
        {waiting.map((a) => (
          <li key={a.envelopeId} className="row-between">
            <span>
              <strong>{a.signerName}</strong>
              <span className="small muted">
                {" "}
                — {a.jobReference}. Signed {longDate(a.signedAt)}.
              </span>
            </span>
            <button
              type="button"
              disabled={busy}
              onClick={() => void countersign(a.envelopeId)}
            >
              {signing === a.envelopeId ? "Countersigning…" : "Countersign"}
            </button>
          </li>
        ))}
      </ul>
      <Failure error={error} what="that countersignature" />
    </>
  );
}
